import React, { useEffect, useMemo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { Caption1, Label, RocketSmallIcon } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useProjectProgressVisibility, useSecurity } from "~/components/Hooks";
import { loadTask } from "~/store/actions/actions.tasks.jsx";
import { getConfigOption, getRawConfigOption } from "~/store/selectors/selector.configOptions.jsx";
import { getProjectById } from "~/store/selectors/selector.projects.jsx";
import { getTaskById } from "~/store/selectors/selector.tasks.jsx";
import { isLoggedUserClientSelector } from "~/store/selectors/selector.users.jsx";
import { useTaskFormContext } from "./context/TaskFormContext.js";
import TaskPageContext from "./context/TaskPageContext.jsx";
import DependenciesBodyContainer from "./Dependencies/DependenciesBodyContainer.jsx";
import Header from "./Header.js";
import { default as TaskBillableStatusContainer } from "./TaskBillableStatus.jsx";
import TaskFormSubmitContainer from "./TaskFormSubmitContainer.jsx";
import TaskPropertiesWrapper from "./TaskProperties/TaskPropertiesWrapper.jsx";
import { TaskRealtime } from "./TaskRealTIme.js";
import { CallToUpgradeLink } from "../CallToUpgradeLink/CallToUpgradeLink.js";
import { TaskCostSummary } from "../CostSummary/TaskCostSummary.js";
import {
  ObjectSubscribersPanel,
  Attachments,
  DescriptionWrapper,
  Divider,
  ObjectPropertiesWrapper,
  ObjectWrapper,
} from "../ModalObjects/";
import Comments from "../ModalObjects/Comments.js";
import ObjectHistory from "../ObjectHistory/ObjectHistory.jsx";
import RemindersContainer from "../Reminders/RemindersContainer.jsx";
import Subtasks from "../Subtasks/Subtasks.jsx";
import ExpenseTracking from "../Tracking/ExpenseTracking.jsx";
import TimeTracking from "../Tracking/TimeTracking.jsx";
import "../ModalObjects/modal-object.less";

export const Task = ({ projectId, taskId, onRedirect, onClose, onMakeRecurringTask, openInModal, shouldOverflow }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoggedUserClient = useSelector(isLoggedUserClientSelector);
  const { showTaskForm, handleShowEditTaskForm, handleCloseEditTaskForm } = useTaskFormContext();
  const task = useSelector((state) => getTaskById(state, taskId));
  const project = useSelector((state) => getProjectById(state, projectId));
  const taskIsTrashed = task ? task.is_trashed : false;
  const can = useSecurity();
  const canEdit = can(["edit"], task);
  const taskTimeTrackingEnabled = useSelector((state) => getConfigOption(state, `task_time_tracking_enabled`));
  const isTaskDependenciesEnabled = useSelector((state) => getRawConfigOption(state, "task_dependencies_enabled"));
  const expenseTrackingEnabled = useSelector((state) => getConfigOption(state, `expense_tracking_enabled`));
  const profitabilityEnabled = useSelector((state) => getRawConfigOption(state, "profitability_enabled"));

  const [openSummary, setOpenSummary] = useState(false);

  const handleOpenSummary = useCallback(() => {
    setOpenSummary(true);
  }, []);

  const handleCloseSummary = useCallback(() => {
    setOpenSummary(false);
  }, []);

  useEffect(() => {
    return () => {
      if (!taskIsTrashed) {
        dispatch(loadTask(projectId, taskId));
      }
    };
  }, [projectId, taskId, dispatch, taskIsTrashed]);

  const showTracking = useMemo(() => {
    if (project && project.get("is_tracking_enabled")) {
      if (!isLoggedUserClient && (expenseTrackingEnabled || taskTimeTrackingEnabled)) {
        return true;
      }
      if (project.is_client_reporting_enabled && (expenseTrackingEnabled || taskTimeTrackingEnabled)) {
        return true;
      }
      return false;
    }
  }, [isLoggedUserClient, project, expenseTrackingEnabled, taskTimeTrackingEnabled]);

  const handleConvertToTask = useCallback(
    (task) => {
      if (openInModal) {
        navigate(`/projects/${task.project_id}?modal=Task-${task.id}-${task.project_id}`);
      } else {
        navigate(`/projects/${task.project_id}/tasks/${task.id}`);
      }
    },
    [openInModal, navigate]
  );

  const { showProfitabilityProgress } = useProjectProgressVisibility(projectId);

  return (
    <>
      <TaskPageContext.Provider
        value={{
          redirectTo: onRedirect,
          handleClose: onClose,
          onMakeRecurringTask,
        }}
      >
        {showTaskForm ? (
          <div className="tw-p-8 sm:tw-pr-2 tw-w-full">
            <TaskFormSubmitContainer
              id={taskId}
              projectId={projectId}
              onCancel={handleCloseEditTaskForm}
              onSubmit={handleCloseEditTaskForm}
              submitButtonLabel={t("Save")}
              shouldOverflow={shouldOverflow}
            />
          </div>
        ) : (
          <>
            <ObjectWrapper>
              <div className="tw-px-6 tw-pt-6">
                <Header
                  id={taskId}
                  projectId={task?.project_id}
                  isTaskDependenciesEnabled={isTaskDependenciesEnabled}
                  openInModal={openInModal}
                  handleShowEditTaskForm={handleShowEditTaskForm}
                  className="tw-mb-6"
                />
                <DescriptionWrapper
                  parentId={taskId}
                  parentType="Task"
                  body={task?.body_formatted}
                  className={classNames("tw-mb-4 tw-border-b tw-border-solid tw-border-0 tw-border-border-primary", {
                    "tw-pb-4 ": task?.body_formatted,
                  })}
                  onDoubleClick={canEdit ? handleShowEditTaskForm : undefined}
                />
                <Attachments
                  id={taskId}
                  type="Task"
                  className="tw-mb-4 tw-border-b tw-border-solid tw-border-0 tw-border-border-primary"
                />
                <Subtasks
                  taskId={taskId}
                  projectId={projectId}
                  convertToTaskReact={handleConvertToTask}
                />
                {isTaskDependenciesEnabled ? (
                  <DependenciesBodyContainer
                    taskId={taskId}
                    projectId={projectId}
                  />
                ) : null}
              </div>
              <ObjectPropertiesWrapper>
                <TaskPropertiesWrapper id={taskId} />
                {showTracking ? (
                  <>
                    <div className="task_properties">
                      {taskTimeTrackingEnabled ? (
                        <div className="tw-mb-6">
                          <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
                            <Label size="small">{t("Time Tracking")}</Label>
                            {showProfitabilityProgress ? (
                              <>
                                {profitabilityEnabled ? (
                                  <Caption1
                                    color="theme-primary"
                                    className="tw-cursor-pointer"
                                    onClick={handleOpenSummary}
                                  >
                                    {t("Cost Summary")}
                                  </Caption1>
                                ) : (
                                  <CallToUpgradeLink
                                    ownerChildren={
                                      <Caption1
                                        as={RouterLink}
                                        color="theme-primary"
                                        className="tw-cursor-pointer tw-flex tw-items-center tw-justify-between"
                                      >
                                        {t("Cost Summary")}
                                        <RocketSmallIcon className="tw-fill-primary" />
                                      </Caption1>
                                    }
                                    memberChildren={
                                      <Caption1
                                        color="theme-primary"
                                        className="tw-cursor-pointer tw-flex tw-items-center tw-justify-between"
                                      >
                                        {t("Cost Summary")}
                                        <RocketSmallIcon className="tw-fill-primary" />
                                      </Caption1>
                                    }
                                    features={["profitability"]}
                                    context="Task Cost Summary"
                                    page="Task"
                                  />
                                )}
                              </>
                            ) : null}
                          </div>
                          <TimeTracking
                            isLoggedUserClient={isLoggedUserClient}
                            parentType="Task"
                            parentId={taskId}
                            projectId={projectId}
                          />
                        </div>
                      ) : null}
                      {expenseTrackingEnabled ? (
                        <div className="tw-mb-6">
                          <Label
                            size="small"
                            className="tw-mb-1 tw-block"
                          >
                            {t("Expense Tracking")}
                          </Label>
                          <ExpenseTracking
                            isLoggedUserClient={isLoggedUserClient}
                            parentType="Task"
                            parentId={taskId}
                            projectId={projectId}
                          />
                        </div>
                      ) : null}
                      <TaskBillableStatusContainer
                        taskId={taskId}
                        projectId={projectId}
                        className="tw-mb-6"
                      />
                    </div>
                    <Divider className="tw-mt-0 tw-mb-6" />
                  </>
                ) : null}
                <div className="tw-mb-6">
                  <ObjectSubscribersPanel
                    parentId={taskId}
                    hiddenFromClients={task?.is_hidden_from_clients}
                    parentType="Task"
                    canEdit={canEdit}
                    members={project?.members.toArray()}
                  />
                </div>
                <Divider
                  className={classNames("tw-mt-0", {
                    "lg:tw-hidden tw-mb-0": isLoggedUserClient,
                  })}
                />
                {!isLoggedUserClient ? (
                  <>
                    <RemindersContainer
                      parent={task}
                      members={project?.members.toArray()}
                    />
                    <Divider className="lg:tw-hidden tw-mb-0" />
                  </>
                ) : null}
              </ObjectPropertiesWrapper>
              <div className="tw-px-6">
                <Comments
                  hiddenFromClients={task?.is_hidden_from_clients}
                  parentId={taskId}
                  parentType="Task"
                  commentsCount={task?.comments_count}
                  projectId={projectId}
                />
                <ObjectHistory
                  objectId={taskId}
                  objectType="Task"
                  projectId={projectId}
                />
              </div>
            </ObjectWrapper>
          </>
        )}
      </TaskPageContext.Provider>
      {!openInModal ? (
        <TaskRealtime
          projectId={projectId}
          id={taskId}
        />
      ) : null}
      {openSummary ? (
        <TaskCostSummary
          open={openSummary}
          onClose={handleCloseSummary}
          projectId={projectId}
          taskId={taskId}
        />
      ) : null}
    </>
  );
};

Task.propTypes = {
  projectId: PropTypes.number.isRequired,
  taskId: PropTypes.number.isRequired,
  onRedirect: PropTypes.func,
  onClose: PropTypes.func,
  onMakeRecurringTask: PropTypes.func,
  openInModal: PropTypes.bool,
  shouldOverflow: PropTypes.bool,
};

Task.defaultProps = {
  openInModal: false,
};
